import { useState, useEffect, useMemo, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';

// LEAFLET IMPORTS
import 'leaflet/dist/leaflet.css';
import { MapContainer, TileLayer, Marker, Popup, AttributionControl } from 'react-leaflet';
import L from 'leaflet';

import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

const DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34]
});
L.Marker.prototype.options.icon = DefaultIcon;

import { 
  // Icônes Perso
  Clock, 
  Calendar, 
  MapPin, 
  FileText, 
  Users,
  // Icônes Gestion
  TrendingUp,
  FolderKanban,
  UserPlus,
  ShoppingCart,
  FolderSearch,
  LayoutDashboard,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Briefcase
} from 'lucide-react';

// --- TYPES ---
interface ProjectStat {
  id: string;
  code: string;
  name: string;
  budget: number;
  consumed: number;
  percentage: number;
}

interface WorkingItem { name: string; project: string; projectName?: string; period: string; lat?: number; lng?: number; }
// Suppression de 'absent' dans l'interface car non utilisé pour l'affichage ici
interface DailyStatus { working: WorkingItem[]; }

interface LeaveQueryResult {
  type: string;
  start_date: string;
  end_date: string;
  employee: { id: string; full_name: string } | null;
}

interface AssignmentQueryResult {
  period: string;
  assignment_date: string;
  employee: { id: string; full_name: string } | null;
  project: { code: string; name: string; lat: number | null; lng: number | null } | null;
}

interface PersoCardProps {
  title: string;
  subtitle: string;
  icon: ReactNode;
  color: string;
  bg: string;
  onClick: () => void;
}

interface AdminBtnProps {
  title: string;
  icon: ReactNode;
  color: string;
  onClick: () => void;
}

// --- HELPERS ---
const getStartOfWeek = (d: Date) => {
  const date = new Date(d);
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1); 
  return new Date(date.setDate(diff));
};

const getEndOfWeek = (d: Date) => {
  const date = getStartOfWeek(d);
  date.setDate(date.getDate() + 6);
  return date;
};

const formatDateDB = (d: Date) => d.toISOString().split('T')[0];

const getISOWeekNumber = (d: Date): number => {
  const date = new Date(d.getTime());
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + 3 - (date.getDay() + 6) % 7);
  const week1 = new Date(date.getFullYear(), 0, 4);
  return 1 + Math.round(((date.getTime() - week1.getTime()) / 86400000 - 3 + (week1.getDay() + 6) % 7) / 7);
};

const computeMergedPeriodStyle = (periods: string[]) => {
  const pSet = new Set(periods.map(p => p.toUpperCase()));
  const hasMorning = pSet.has('AM') || pSet.has('Q1') || pSet.has('Q2');
  const hasAfternoon = pSet.has('PM') || pSet.has('Q3') || pSet.has('Q4');
  const hasFullDay = pSet.has('DAY');

  if (hasFullDay || (hasMorning && hasAfternoon)) return { label: 'Journée', bg: '#f1f5f9', color: '#475569', border: '#cbd5e1', sortOrder: 0 };
  if (hasMorning) return { label: 'Matin', bg: '#fff7ed', color: '#ea580c', border: '#fed7aa', sortOrder: 1 };
  if (hasAfternoon) return { label: 'Aprem', bg: '#eff6ff', color: '#2563eb', border: '#bfdbfe', sortOrder: 2 };
  return { label: '?', bg: '#f1f5f9', color: '#94a3b8', border: '#e2e8f0', sortOrder: 3 };
};

export default function BEDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [projects, setProjects] = useState<ProjectStat[]>([]);
  const [daily, setDaily] = useState<DailyStatus>({ working: [] });
  const [viewMode, setViewMode] = useState<'day' | 'week'>('day');
  const [currentDate, setCurrentDate] = useState(new Date());

  const dateRange = useMemo(() => {
    if (viewMode === 'day') {
      const d = formatDateDB(currentDate);
      const weekNum = getISOWeekNumber(currentDate);
      return { start: d, end: d, label: `Semaine ${weekNum} - ${currentDate.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric' })}` };
    } else {
      const start = getStartOfWeek(currentDate);
      const end = getEndOfWeek(currentDate);
      const weekNum = getISOWeekNumber(start);
      return { start: formatDateDB(start), end: formatDateDB(end), label: `Semaine ${weekNum}` };
    }
  }, [viewMode, currentDate]);

  useEffect(() => {
    const loadData = async () => {
      try {
        // 1. RENTABILITÉ (Top 5 Production)
        const { data: projData } = await supabase
          .from('projects')
          .select('id, code, name, estimated_hours')
          .eq('is_active', true)
          .eq('category', 'production')
          .neq('code', 'SYS-FORM')
          .order('code', { ascending: false })
          .limit(5);

        if (projData) {
          const { data: timeData } = await supabase.from('time_entries')
            .select('project_id, hours_normal, hours_saturday, hours_major')
            .in('project_id', projData.map(p => p.id));

          const computedStats = projData.map(p => {
            const entries = timeData?.filter(t => t.project_id === p.id) || [];
            const consumed = entries.reduce((acc, curr) => acc + curr.hours_normal + curr.hours_saturday + curr.hours_major, 0);
            return {
              ...p,
              budget: p.estimated_hours,
              consumed,
              percentage: p.estimated_hours > 0 ? (consumed / p.estimated_hours) * 100 : 0
            };
          }).sort((a, b) => b.percentage - a.percentage);
          setProjects(computedStats);
        }

        // 2. MÉTÉO & CARTE
        const { data: leavesData } = await supabase.from('leaves')
          .select('type, start_date, end_date, employee:employees!employee_id(id, full_name)')
          .eq('status', 'validated')
          .or(`start_date.lte.${dateRange.end},end_date.gte.${dateRange.start}`);

        const { data: workData } = await supabase.from('project_assignments')
          .select('assignment_date, period, employee:employees(id, full_name), project:projects(code, name, lat, lng)')
          .gte('assignment_date', dateRange.start)
          .lte('assignment_date', dateRange.end);

        const absenceMap = new Set<string>();
        const safeLeaves = (leavesData as unknown as LeaveQueryResult[]) || [];
        safeLeaves.forEach(leave => {
          if (!leave.employee?.id) return;
          const current = new Date(leave.start_date);
          const end = new Date(leave.end_date);
          while (current <= end) {
            absenceMap.add(`${leave.employee.id}_${formatDateDB(current)}`);
            current.setDate(current.getDate() + 1);
          }
        });

        const workingList = ((workData as unknown as AssignmentQueryResult[]) || [])
          .filter(w => w.employee?.id && !absenceMap.has(`${w.employee.id}_${w.assignment_date}`))
          .map(w => ({
            name: w.employee?.full_name || 'Inconnu',
            project: w.project?.code || '?',
            projectName: w.project?.name || '',
            period: w.period,
            lat: w.project?.lat || undefined,
            lng: w.project?.lng || undefined
          }));

        setDaily({
          working: workingList
        });

      } catch (err) { console.error(err); }
    };
    loadData();
  }, [dateRange]);

  const groupedPresence = useMemo(() => {
    const groups: Record<string, { code: string, name: string, people: Record<string, string[]>, lat?: number, lng?: number }> = {};
    daily.working.forEach(item => {
      if (item.project === 'SYS-FORM') return;
      if (!groups[item.project]) groups[item.project] = { code: item.project, name: item.projectName || '', people: {}, lat: item.lat, lng: item.lng };
      if (!groups[item.project].people[item.name]) groups[item.project].people[item.name] = [];
      groups[item.project].people[item.name].push(item.period);
    });
    return Object.values(groups).map(g => ({
      ...g,
      people: Object.entries(g.people).map(([name, periods]) => ({ name, ...computeMergedPeriodStyle(periods) }))
    }));
  }, [daily.working]);

  const handleDateChange = (dir: number) => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + (viewMode === 'day' ? dir : dir * 7));
    setCurrentDate(newDate);
  };

  // Icônes Map
  const createHomeIcon = () => L.divIcon({
    className: 'custom-home-icon',
    html: `<div style="background-color:#2563eb;color:white;width:30px;height:30px;border-radius:50%;display:flex;align-items:center;justify-content:center;border:2px solid white;box-shadow:0 2px 4px rgba(0,0,0,0.2)"><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg></div>`,
    iconSize: [30, 30], iconAnchor: [15, 15]
  });

  const createMapIcon = (count: number) => L.divIcon({
    className: 'custom-map-icon',
    html: `<div style="background-color:#2563eb;color:white;width:30px;height:30px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:12px;border:2px solid white;box-shadow:0 2px 4px rgba(0,0,0,0.2)">${count}</div>`,
    iconSize: [30, 30], iconAnchor: [15, 15]
  });

  return (
    <div style={{ fontFamily: 'Inter, sans-serif', maxWidth: '1600px', margin: '0 auto' }}>
      
      {/* HEADER */}
      <div style={{ marginBottom: '24px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <div>
          <h1 style={{ marginBottom: '4px', fontSize: '1.6rem', color: '#1e293b' }}>Espace Bureau d'Étude</h1>
          <p style={{ color: '#64748b', margin: 0 }}>Bonjour <strong>{user?.full_name}</strong></p>
        </div>
        <div style={{ background: '#eff6ff', color: '#2563eb', padding: '8px 16px', borderRadius: '12px', fontSize: '0.9rem', fontWeight: '600', display:'flex', alignItems:'center', gap:'8px' }}>
          <CalendarDays size={18} />
          {new Date().toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
        </div>
      </div>

      {/* 1. MON ESPACE (Fonctionnalités Perso) */}
      <h2 style={{ fontSize: '1.1rem', color: '#475569', marginBottom: '16px', display:'flex', alignItems:'center', gap:'8px' }}>
        <LayoutDashboard size={20} /> Mon Espace
      </h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '40px' }}>
        <PersoCard title="Mon Planning" subtitle="Ma semaine" icon={<MapPin size={24} />} color="#7e22ce" bg="#f3e8ff" onClick={() => navigate('/planning')} />
        <PersoCard title="Mes Heures" subtitle="Saisie hebdo" icon={<Clock size={24} />} color="#2563eb" bg="#eff6ff" onClick={() => navigate('/timesheet')} />
        <PersoCard title="Mes Absences" subtitle="Congés, Maladie..." icon={<Calendar size={24} />} color="#10b981" bg="#ecfdf5" onClick={() => navigate('/leaves')} />
        <PersoCard title="Mes Documents" subtitle="Feuilles validées" icon={<FileText size={24} />} color="#d97706" bg="#fffbeb" onClick={() => navigate('/documents')} />
        <PersoCard title="Mon Équipe" subtitle="Intérimaires" icon={<Users size={24} />} color="#475569" bg="#f1f5f9" onClick={() => navigate('/team')} />
      </div>

      {/* 2. PILOTAGE TECHNIQUE & GESTION (Fonctionnalités Admin) */}
      <h2 style={{ fontSize: '1.1rem', color: '#475569', marginBottom: '16px', display:'flex', alignItems:'center', gap:'8px' }}>
        <Briefcase size={20} /> Pilotage Technique & Gestion
      </h2>
      
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px' }}>
        
        {/* GAUCHE : CARTE & LISTE */}
        <div style={{ flex: '2 1 600px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
          <div style={{ background: 'white', borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
            {/* Header Carte */}
            <div style={{ padding: '14px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent:'space-between', alignItems:'center', background:'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)' }}>
              <div style={{ fontWeight:'600', color:'#0f172a', fontSize:'0.95rem' }}>Localisation des équipes</div>
              <div style={{ display:'flex', gap:'12px', alignItems:'center' }}>
                <button
                  onClick={() => setViewMode(viewMode === 'day' ? 'week' : 'day')}
                  style={{
                    fontSize:'0.8rem',
                    padding:'6px 12px',
                    borderRadius:'8px',
                    border:'1px solid #e2e8f0',
                    cursor:'pointer',
                    background:'white',
                    fontWeight:'500',
                    color:'#475569',
                    boxShadow:'0 1px 2px rgba(0,0,0,0.04)'
                  }}
                >
                  {viewMode === 'day' ? 'Vue Jour' : 'Vue Semaine'}
                </button>
                <div style={{ display:'flex', alignItems:'center', gap:'4px', background:'white', padding:'4px 8px', borderRadius:'8px', border:'1px solid #e2e8f0', boxShadow:'0 1px 2px rgba(0,0,0,0.04)' }}>
                  <button onClick={() => handleDateChange(-1)} style={{ border:'none', background:'transparent', cursor:'pointer', padding:'4px', borderRadius:'4px', display:'flex', alignItems:'center' }}><ChevronLeft size={18} color="#475569"/></button>
                  <span style={{ fontSize:'0.85rem', fontWeight:'600', minWidth:'180px', textAlign:'center', color:'#1e293b' }}>{dateRange.label}</span>
                  <button onClick={() => handleDateChange(1)} style={{ border:'none', background:'transparent', cursor:'pointer', padding:'4px', borderRadius:'4px', display:'flex', alignItems:'center' }}><ChevronRight size={18} color="#475569"/></button>
                </div>
              </div>
            </div>

            {/* Map */}
            <div style={{ height: '350px', width: '100%' }}>
              <MapContainer center={[47.962, 0.203]} zoom={9} style={{ height: '100%', width: '100%' }} attributionControl={false}>
                <AttributionControl prefix={false} />
                <TileLayer url="https://tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='© OSM' />
                <Marker position={[48.029, 0.176]} icon={createHomeIcon()}><Popup>Siège</Popup></Marker>
                {groupedPresence.map(s => s.lat && s.lng && (
                  <Marker key={s.code} position={[s.lat, s.lng]} icon={createMapIcon(s.people.length)}>
                    <Popup>
                      <div style={{ fontWeight:'700', fontSize:'0.95rem', marginBottom:'2px' }}>{s.name}</div>
                      <div style={{ fontSize:'0.75rem', color:'#64748b', marginBottom:'6px', fontFamily:'monospace' }}>{s.code}</div>
                      <div style={{ display:'flex', flexDirection:'column', gap:'2px' }}>
                        {s.people.map((p,i) => (
                          <span key={i} style={{ fontSize:'0.8rem', background:p.bg, color:p.color, padding:'2px 6px', borderRadius:'4px', border:`1px solid ${p.border}` }}>{p.name}</span>
                        ))}
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </div>

            {/* Liste AFFECTATIONS */}
            <div style={{ padding:'16px', background:'white' }}>
              <div style={{ fontWeight:'600', color:'#475569', fontSize:'0.85rem', marginBottom:'12px', textTransform:'uppercase', letterSpacing:'0.5px' }}>Affectations</div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(280px, 1fr))', gap:'12px' }}>
                {groupedPresence.map((g, i) => (
                  <div key={i} style={{ padding:'14px', background:'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)', borderRadius:'12px', border:'1px solid #e2e8f0', boxShadow:'0 1px 3px rgba(0,0,0,0.04)' }}>
                    <div style={{ fontWeight:'700', fontSize:'1rem', color:'#1e293b', marginBottom:'2px' }}>{g.name || 'Chantier sans nom'}</div>
                    <div style={{ fontSize:'0.75rem', color:'#94a3b8', marginBottom:'10px', fontFamily:'monospace' }}>{g.code}</div>
                    <div style={{ display:'flex', flexWrap:'wrap', gap:'6px' }}>
                      {g.people.map((p, idx) => (
                        <span key={idx} style={{
                          fontSize:'0.8rem',
                          background: p.bg,
                          color: p.color,
                          padding:'4px 10px',
                          borderRadius:'6px',
                          border:`1px solid ${p.border}`,
                          fontWeight:'500'
                        }}>
                          {p.name}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
                {groupedPresence.length === 0 && <div style={{ color:'#94a3b8', fontStyle:'italic', padding:'20px', textAlign:'center' }}>Aucune affectation pour cette période.</div>}
              </div>
            </div>
          </div>
        </div>

        {/* DROITE : RENTABILITÉ & ACTIONS GESTION */}
        <div style={{ flex: '1 1 300px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
          
          {/* Rentabilité */}
          <div style={{ background: 'white', borderRadius: '16px', border: '1px solid #e2e8f0', padding:'20px', boxShadow:'0 1px 3px rgba(0,0,0,0.04)' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'16px', paddingBottom:'12px', borderBottom:'1px solid #f1f5f9' }}>
              <h3 style={{ margin:0, fontSize:'1rem', display:'flex', gap:'8px', alignItems:'center', color:'#1e293b' }}><TrendingUp size={18} style={{ color:'#10b981' }} /> Rentabilité Chantiers</h3>
              <button onClick={() => navigate('/admin/reporting')} style={{ fontSize:'0.8rem', color:'#2563eb', border:'none', background:'transparent', cursor:'pointer', fontWeight:'500' }}>Voir tout →</button>
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:'14px' }}>
              {projects.map(p => (
                <div key={p.id} style={{ padding:'10px', background:'#f8fafc', borderRadius:'8px' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'6px' }}>
                    <div>
                      <div style={{ fontWeight:'600', fontSize:'0.9rem', color:'#1e293b' }}>{p.name}</div>
                      <div style={{ fontSize:'0.7rem', color:'#94a3b8', fontFamily:'monospace' }}>{p.code}</div>
                    </div>
                    <span style={{
                      color: p.percentage > 100 ? '#ef4444' : p.percentage > 80 ? '#f59e0b' : '#10b981',
                      fontWeight:'700',
                      fontSize:'0.9rem',
                      background: p.percentage > 100 ? '#fef2f2' : p.percentage > 80 ? '#fffbeb' : '#ecfdf5',
                      padding:'2px 8px',
                      borderRadius:'6px'
                    }}>{p.percentage.toFixed(0)}%</span>
                  </div>
                  <div style={{ width:'100%', height:'8px', background:'#e2e8f0', borderRadius:'4px', overflow:'hidden' }}>
                    <div style={{
                      width: `${Math.min(p.percentage, 100)}%`,
                      height:'100%',
                      background: p.percentage > 100 ? '#ef4444' : p.percentage > 80 ? '#f59e0b' : '#10b981',
                      borderRadius:'4px',
                      transition:'width 0.3s ease'
                    }}></div>
                  </div>
                </div>
              ))}
              {projects.length === 0 && <div style={{ color:'#94a3b8', fontStyle:'italic', textAlign:'center', padding:'20px' }}>Aucun chantier à afficher</div>}
            </div>
          </div>

          {/* Outils de Gestion */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' }}>
            <AdminBtn title="Planificateur" icon={<CalendarDays size={24}/>} color="#2563eb" onClick={() => navigate('/admin/planning-edit')} />
            <AdminBtn title="Chantiers" icon={<FolderKanban size={24}/>} color="#6366f1" onClick={() => navigate('/admin/projects')} />
            <AdminBtn title="Employés" icon={<UserPlus size={24}/>} color="#8b5cf6" onClick={() => navigate('/admin/employees')} />
            <AdminBtn title="Matériel" icon={<ShoppingCart size={24}/>} color="#f59e0b" onClick={() => navigate('/admin/materials')} />
            <AdminBtn title="Affaires Diverses" icon={<FolderSearch size={24}/>} color="#ec4899" onClick={() => navigate('/admin/divers')} />
          </div>

        </div>
      </div>
    </div>
  );
}

// SOUS-COMPOSANTS
function PersoCard({ title, subtitle, icon, color, bg, onClick }: PersoCardProps) {
  return (
    <div onClick={onClick} style={{ 
      background: 'white', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '16px', 
      cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '15px', transition: 'transform 0.2s', boxShadow:'0 1px 2px rgba(0,0,0,0.05)'
    }} onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'} onMouseLeave={e => e.currentTarget.style.transform = 'none'}>
      <div style={{ background: bg, color: color, padding: '10px', borderRadius: '12px' }}>{icon}</div>
      <div>
        <div style={{ fontWeight: '700', color: '#1e293b' }}>{title}</div>
        <div style={{ fontSize: '0.8rem', color: '#64748b' }}>{subtitle}</div>
      </div>
    </div>
  );
}

function AdminBtn({ title, icon, color, onClick }: AdminBtnProps) {
  return (
    <div onClick={onClick} style={{ 
      background: 'white', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '16px', 
      cursor: 'pointer', display: 'flex', flexDirection:'column', alignItems: 'center', gap: '10px', textAlign:'center', transition: 'all 0.2s'
    }} onMouseEnter={e => e.currentTarget.style.borderColor = color} onMouseLeave={e => e.currentTarget.style.borderColor = '#e2e8f0'}>
      <div style={{ color: color, background: `${color}15`, padding: '10px', borderRadius: '50%' }}>{icon}</div>
      <div style={{ fontWeight: '600', fontSize:'0.9rem', color: '#1e293b' }}>{title}</div>
    </div>
  );
}